// src/components/AudioPlayer/YouTubeAudioPlayer.js
import React, { useEffect, useRef, useState } from 'react';
import './AudioPlayer.css';
import InstructionModal from './InstructionModal'; // ✅ only if not already imported


function YouTubeAudioPlayer({ videoId, visualizerType, isVisualizerEnabled }) {
  const playerRef = useRef(null);
  const playerInstance = useRef(null);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [showHelp, setShowHelp] = useState(false);


  // Initialize YouTube IFrame API and create the player.
  useEffect(() => {
    const onYouTubeIframeAPIReady = () => {
      playerInstance.current = new window.YT.Player(playerRef.current, {
        height: '0',
        width: '0',
        videoId: videoId,
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          modestbranding: 1,
          playsinline: 1,
          fs: 0,
          rel: 0,
          showinfo: 0,
        },
        events: {
          onReady: onPlayerReady,
          onStateChange: onPlayerStateChange,
        },
      });
    };

    const onPlayerReady = () => {
      // Player ready.
    };

    const onPlayerStateChange = (event) => {
      if (event.data === window.YT.PlayerState.PLAYING) {
        const updateProgress = () => {
          const duration = playerInstance.current.getDuration() || 0;
          const currentTime = playerInstance.current.getCurrentTime() || 0;
          setProgress((currentTime / duration) * 100);
        };
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(updateProgress, 100);
        if (isVisualizerEnabled) {
          startVisualizer();
        }
      } else {
        clearInterval(intervalRef.current);
        cancelAnimationFrame(animationRef.current);
      }
    };

    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
      document.head.appendChild(tag);
    } else {
      onYouTubeIframeAPIReady();
    }

    return () => {
      if (playerInstance.current && playerInstance.current.destroy) {
        playerInstance.current.destroy();
      }
      if (intervalRef.current) clearInterval(intervalRef.current);
      cancelAnimationFrame(animationRef.current);
    };
  }, [videoId]);

  // When visualizer settings change, restart the visualizer.
  useEffect(() => {
    if (isVisualizerEnabled) {
      startVisualizer();
    } else {
      cancelAnimationFrame(animationRef.current);
    }
  }, [visualizerType, isVisualizerEnabled]);

  // Simulate frequency data using a sine wave based on current time.
  const simulateFrequencyData = (bufferLength) => {
    const dataArray = new Uint8Array(bufferLength);
    const now = Date.now();
    for (let i = 0; i < bufferLength; i++) {
      // Oscillate between 0 and 255.
      dataArray[i] = 128 + 127 * Math.sin((now / 300) + (i * 0.5));
    }
    return dataArray;
  };

  const startVisualizer = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const bufferLength = 32; // simulate 32 frequency bins

    // Resize canvas to match its display size.
    const resizeCanvas = () => {
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      const dataArray = simulateFrequencyData(bufferLength);
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (visualizerType === 'pulse') {
        // Compute average amplitude and draw a pulsing circle.
        const avg = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
        const radius = (avg / 255) * (canvas.height / 2);
        ctx.beginPath();
        ctx.arc(canvas.width / 2, canvas.height / 2, radius, 0, 2 * Math.PI);
        ctx.fillStyle = '#6EE7B7';
        ctx.fill();
      } else if (visualizerType === 'bars') {
        const barWidth = (canvas.width / bufferLength) * 2.5;
        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
          const barHeight = (dataArray[i] / 255) * canvas.height;
          ctx.fillStyle = '#6EE7B7';
          ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
          x += barWidth + 1;
        }
      } else if (visualizerType === 'waves') {
        ctx.beginPath();
        const sliceWidth = canvas.width / bufferLength;
        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
          const y = canvas.height - (dataArray[i] / 255) * canvas.height;
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
          x += sliceWidth;
        }
        ctx.strokeStyle = '#6EE7B7';
        ctx.stroke();
      } else if (visualizerType === 'circle') {
        const avg = dataArray.reduce((a, b) => a + b, 0) / bufferLength;
        const radius = (avg / 255) * (canvas.height / 2);
        ctx.beginPath();
        ctx.arc(canvas.width / 2, canvas.height / 2, radius, 0, 2 * Math.PI);
        ctx.fillStyle = '#6EE7B7';
        ctx.fill();
      } else if (visualizerType === 'dots') {
        const dotRadius = 5;
        const spacing = canvas.width / bufferLength;
        for (let i = 0; i < bufferLength; i++) {
          const x = spacing * i;
          const y = canvas.height - (dataArray[i] / 255) * canvas.height;
          ctx.beginPath();
          ctx.arc(x, y, dotRadius, 0, 2 * Math.PI);
          ctx.fillStyle = '#6EE7B7';
          ctx.fill();
        }
      } else if (visualizerType === 'spectrum') {
        const barWidth = canvas.width / bufferLength;
        const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, '#6EE7B7');
        gradient.addColorStop(1, '#ff5555');
        for (let i = 0; i < bufferLength; i++) {
          const barHeight = (dataArray[i] / 255) * canvas.height;
          ctx.fillStyle = gradient;
          ctx.fillRect(i * barWidth, canvas.height - barHeight, barWidth, barHeight);
        }
      }
    };

    draw();
    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', resizeCanvas);
    };
  };

  const playAudio = () => {
    playerInstance.current?.playVideo();
  };

  const pauseAudio = () => {
    playerInstance.current?.pauseVideo();
  };

  return (
    <div className="audio-player">
      <div ref={playerRef} className="player"></div>
      <div className="controls">
  <button onClick={playAudio}>Play</button>
  <button onClick={pauseAudio}>Pause</button>
  <button onClick={() => setShowHelp(true)}>Help</button>
  {showHelp && <InstructionModal onClose={() => setShowHelp(false)} />}
</div>

      <div className="progress-bar">
        <div className="progress" style={{ width: `${progress}%` }}></div>
      </div>
      {isVisualizerEnabled && (
        <div className={`visualizer ${visualizerType}`}>
          <canvas ref={canvasRef}></canvas>
        </div>
      )}
    </div>
  );
}

export default YouTubeAudioPlayer;
